import type { ExperimentConfig } from '@/types';

const experiment10Config: ExperimentConfig = {
  id: 'exp10',
  number: 10,
  name: 'Chemical Equilibrium',
  description: 'Explore chemical equilibrium through qualitative observations using Le Chatelier\'s Principle and quantitative measurements for Ka determination. Observe equilibrium shifts and calculate dissociation constants.',
  estimatedTime: '90 minutes',
  learningObjectives: [
    'Observe and explain equilibrium shifts using Le Chatelier\'s Principle',
    'System A: FeSCN²⁺ equilibrium — mix 10 mL each of 0.01 M FeCl₃ and 0.01 M KSCN, test with drops of 1 M FeCl₃ and 1 M KSCN',
    'System A: Observe blood red colour of FeSCN²⁺ — Fe³⁺(aq) + SCN⁻(aq) ⇌ FeSCN²⁺(aq)',
    'System B: Iodine equilibrium — I₂(aq) + H₂O(l) ⇌ 2H⁺(aq) + I⁻(aq) + IO⁻(aq), add NaOH to observe colour change',
    'System B (Common Ion): Add ~10 drops conc. HCl to 10 mL saturated NaCl — observe NaCl precipitate',
    'Determine Ka of acetic acid from pH measurements (three measurements each, calculate average Ka)',
    'Part II(A): Measure pH of 1.0 M, 0.5 M, 0.2 M, and 0.1 M acetic acid solutions (each 6.0 mL in 10 mL beaker)',
    'Part II(B): Investigate effect of added potassium acetate on acetic acid dissociation',
    'Part II(C): Determine Ka for bisulfate ion (HSO₄⁻) using 0.1 M KHSO₄',
    'Calculate Ka from pH: Ka = [H₃O⁺][A⁻] / [HA], where [H₃O⁺] = 10⁻ᵖᴴ',
  ],
  safetyNotes: [
    'Handle acids and bases with care — HCl is corrosive',
    'FeCl₃ solutions stain skin and clothing',
    'KSCN (thiocyanate) solutions are toxic - avoid ingestion',
    'Concentrated HCl fumes — use in ventilated area',
    'Wash hands thoroughly after handling chemicals',
  ],
  requiredEquipment: [
    'Test tube rack',
    'Test tubes',
    'pH meter',
    'Beakers (10 mL, 150 mL)',
    'Graduated cylinders',
    'Pasteur pipette',
    'Stirring rods',
    'Wash bottle',
    '0.01 M iron (III) chloride (FeCl₃)',
    '0.01 M potassium thiocyanate (KSCN)',
    '1 M iron (III) chloride (FeCl₃)',
    '1 M potassium thiocyanate (KSCN)',
    'Dilute sodium hydroxide solution (NaOH)',
    'Iodine solution (I₂ in aqueous solution)',
    'Concentrated HCl',
    'Saturated NaCl solution',
    '1.0 M acetic acid',
    '1.0 M potassium acetate',
    '0.1 M potassium bisulphate (KHSO₄)',
    'Deionised water',
  ],
  parts: [
    {
      id: 0,
      name: 'summary',
      title: 'Summary',
      steps: [],
      equipment: [],
      initialState: {},
    },
    {
      id: 1,
      name: 'leChatelier',
      title: 'Part I: Le Chatelier\'s Principle',
      equipment: [
        'Test tube rack',
        'Test tubes',
        'Dropper bottles',
        'Stirring rods'
      ],
      initialState: {
        currentSystem: 'FeSCN',
        tubes: {
          mixture: {
            color: '#B22222',
            description: '10 mL each of 0.01 M FeCl₃ and 0.01 M KSCN',
            Fe3: 0.01,
            SCN: 0.01
          },
          half1: {
            color: '#B22222',
            additions: ['drops of 1 M FeCl₃'],
            Fe3: 0.01,
            SCN: 0.01
          },
          half2: {
            color: '#B22222',
            additions: ['drops of 1 M KSCN'],
            Fe3: 0.01,
            SCN: 0.01
          }
        },
        predictions: [],
        observations: []
      },
      steps: [
        {
          id: 1,
          title: 'Prepare Reference Solution',
          shortDescription: 'Mix together 10 mL each of 0.01 M FeCl₃ and 0.01 M KSCN solutions',
          handler: 'handlePrepareReference',
          infoContent: {
            title: 'The FeSCN²⁺ Equilibrium',
            content: 'Mix together **10 mL each** of **0.01 M** solutions of iron (III) chloride and potassium thiocyanate. The **blood red** colouration that results is due to the formation of FeSCN²⁺.',
            chemicalEquation: 'Fe³⁺(aq) + SCN⁻(aq) ⇌ FeSCN²⁺(aq)',
            whatHappens: 'Mixing FeCl3 and KSCN produces the blood-red FeSCN2+ complex, establishing a visible equilibrium that can be shifted by adding more reactants.',
            observe: [
              'The mixture turns blood red immediately upon combining the two solutions',
              'The red colour is due to the FeSCN2+ complex ion',
              'This reference colour intensity will be compared against shifted equilibria',
            ],
          }
        },
        {
          id: 2,
          title: 'Predict: Add Fe3+',
          shortDescription: 'Predict the direction of equilibrium shift when Fe3+ is added',
          handler: 'handlePredictFe3'
        },
        {
          id: 3,
          title: 'Add Fe3+ and Observe',
          shortDescription: 'Add drops of 1 M FeCl₃ solution to one portion and observe colour change',
          handler: 'handleAddFe3',
          infoContent: {
            title: 'Le Chatelier\'s Principle',
            content: 'Divide the equilibrium mixture into halves. Add drops of **1 M FeCl₃** to one portion. Adding Fe³⁺ (a reactant) shifts equilibrium to the right, producing more FeSCN²⁺ (darker red).',
            whatHappens: 'Adding concentrated Fe3+ increases the reactant concentration, shifting the equilibrium to the right (toward products) and producing more FeSCN2+, which intensifies the red colour.',
            observe: [
              'The solution becomes a noticeably darker red after adding drops of 1 M FeCl3',
              'Compare the colour intensity to the untreated reference solution',
              'The darker colour confirms the equilibrium shifted to the right (more product)',
            ],
          }
        },
        {
          id: 4,
          title: 'Predict: Add SCN-',
          shortDescription: 'Predict the direction of equilibrium shift when SCN- is added',
          handler: 'handlePredictSCN'
        },
        {
          id: 5,
          title: 'Add SCN- and Observe',
          shortDescription: 'Add drops of 1 M KSCN solution to the other portion and observe colour change',
          handler: 'handleAddSCN'
        },
        {
          id: 6,
          title: 'Compare FeSCN²⁺ Results',
          shortDescription: 'Compare the effect of adding Fe³⁺ vs SCN⁻ on the blood-red colour intensity',
          handler: 'handleCompareAll',
          infoContent: {
            title: 'Expected Results',
            content: `Adding more concentrated FeCl₃ or KSCN both increase the intensity of the blood-red colour, as both shift the equilibrium **to the right** producing more FeSCN²⁺.

| Portion | Addition | Expected Effect |
|---------|----------|----------------|
| Half 1  | drops of 1 M FeCl₃  | Darker red (shift right) |
| Half 2  | drops of 1 M KSCN   | Darker red (shift right) |`,
            whatHappens: 'Both Fe3+ and SCN- additions shift the equilibrium to the right, confirming Le Chatelier\'s Principle: the system counteracts the stress by consuming the added reactant.',
            observe: [
              'Both portions become darker red compared to the original mixture',
              'Adding Fe3+ or SCN- both produce the same directional shift (more product)',
              'Compare the two portions side by side to see if one shift is more pronounced',
            ],
          },
        },
        {
          id: 7,
          title: 'Iodine Equilibrium System',
          shortDescription: 'Add drops of dilute NaOH to a solution of iodine and observe colour change',
          handler: 'handleIodineSystem',
          infoContent: {
            title: 'Iodine Equilibrium (System A.1)',
            content: `Add drops of **dilute sodium hydroxide** solution to a solution of iodine in a test tube and observe any colour change.

In an aqueous solution of iodine, the following equilibrium is established:

**I₂(aq) + H₂O(l) ⇌ 2H⁺(aq) + I⁻(aq) + IO⁻(aq)**

Any colour in the solution can be regarded as entirely due to the presence of I₂(aq). The OH⁻(aq) ions from the NaOH will react with the H⁺(aq) ions present to form unionized water. Explain the observed colour change. What should be added to swing the equilibrium in the opposite direction? Carry out a test to see if this is correct.`,
            chemicalEquation: 'I₂(aq) + H₂O(l) ⇌ 2H⁺(aq) + I⁻(aq) + IO⁻(aq)',
            whatHappens: 'Adding NaOH removes H+ ions (neutralization), shifting the iodine equilibrium to the right, which consumes I2 and causes the brown iodine colour to fade.',
            observe: [
              'The brown/amber iodine colour fades or disappears after adding NaOH drops',
              'OH- neutralizes H+ on the product side, pulling the equilibrium to the right',
              'Adding acid (HCl) should reverse the shift and restore the brown colour',
            ],
          }
        },
        {
          id: 8,
          title: 'Common Ion Effect (System B)',
          shortDescription: 'Add approx. 10 drops of conc. HCl to 10 mL saturated NaCl solution using Pasteur pipette',
          handler: 'handleCommonIonEffect',
          infoContent: {
            title: 'Common Ion Effect on Solubility',
            content: `Add a **10 mL** sample of a saturated NaCl solution to a test tube. Using a **Pasteur pipette**, add approx. **10 drops of conc. HCl**. Observe the formation of any precipitate.

**Observe:** White NaCl crystals precipitate out of solution!

The Cl⁻ from HCl is a "common ion" that increases [Cl⁻], shifting the dissolution equilibrium to the **left**, causing NaCl to precipitate. If a precipitate does form, explain why.`,
            chemicalEquation: 'NaCl(s) ⇌ Na⁺(aq) + Cl⁻(aq)',
            whatHappens: 'Concentrated HCl introduces excess Cl- (common ion) to the saturated NaCl solution, shifting the dissolution equilibrium to the left and causing NaCl crystals to precipitate.',
            observe: [
              'White NaCl crystals precipitate out of the previously clear saturated solution',
              'The precipitate appears as fine white particles or crystals',
              'This demonstrates the common ion effect: excess Cl- decreases NaCl solubility',
            ],
          }
        }
      ]
    },
    {
      id: 2,
      name: 'kaDetermination',
      title: 'Part II: Ka Determination',
      equipment: [
        'pH meter',
        '10 mL beakers (clean, dry)',
        '150 mL beakers',
        'Graduated cylinders',
        'Stirring rods',
      ],
      initialState: {
        pHMeter: {
          isCalibrated: false,
          currentReading: null,
          temperature: 25.0,
          calibrationPoints: { pH4: false, pH7: false, pH10: false }
        },
        selectedAcid: 'acetic',
        dilutions: [
          { concentration: 1.0, pH: null, Ka: null, description: '6.0 mL of 1.0 M acetic acid directly' },
          { concentration: 0.5, pH: null, Ka: null, description: '10 mL of 1.0 M diluted to 20 mL, then 6.0 mL' },
          { concentration: 0.2, pH: null, Ka: null, description: '4.0 mL of 1.0 M diluted to 20 mL, then 6.0 mL' },
          { concentration: 0.1, pH: null, Ka: null, description: '2.0 mL of 1.0 M diluted to 20 mL, then 6.0 mL' }
        ],
        averageKa: null,
        literatureKa: 1.76e-5,
        percentError: null
      },
      steps: [
        {
          id: 1,
          title: 'Calibrate pH Meter',
          shortDescription: 'Calibrate the pH meter using buffer solutions (pH 4, 7, 10)',
          handler: 'handleCalibratePH',
          infoContent: {
            title: 'pH Meter Calibration',
            content: 'The pH meter must be calibrated before use. A two-point calibration (pH 4 and 7) sets the zero and slope. pH 10 buffer can verify accuracy.',
            whatHappens: 'The pH meter electrode is calibrated with standard buffer solutions to ensure accurate pH measurements for the Ka determination.',
            observe: [
              'Calibrate first with pH 7 buffer (sets the zero point), then with pH 4 buffer',
              'Rinse the electrode with deionised water between buffer solutions',
              'The pH meter reading should match the buffer value before proceeding',
            ],
          }
        },
        {
          id: 2,
          title: 'Prepare Dilution Series',
          shortDescription: 'Prepare acetic acid dilutions: 1.0 M, 0.5 M (10/20), 0.2 M (4/20), 0.1 M (2/20)',
          handler: 'handlePrepareDilutions'
        },
        {
          id: 3,
          title: 'Measure pH: 1.0 M',
          shortDescription: 'Place 6.0 mL of 1.0 M acetic acid in 10 mL beaker, measure pH three times, calculate Ka',
          handler: 'handleMeasurePH_1M',
          validation: {
            requires: ['pHMeter.isCalibrated']
          }
        },
        {
          id: 4,
          title: 'Measure pH: 0.5 M',
          shortDescription: 'Dilute 10 mL of 1.0 M acetic acid to 20 mL, place 6.0 mL in clean dry 10 mL beaker, measure pH three times',
          handler: 'handleMeasurePH_05M'
        },
        {
          id: 5,
          title: 'Measure pH: 0.2 M',
          shortDescription: 'Dilute 4.0 mL of 1.0 M acetic acid to 20 mL, place 6.0 mL in clean dry 10 mL beaker, measure pH three times',
          handler: 'handleMeasurePH_02M'
        },
        {
          id: 6,
          title: 'Measure pH: 0.1 M',
          shortDescription: 'Dilute 2.0 mL of 1.0 M acetic acid to 20 mL, place 6.0 mL in clean dry 10 mL beaker, measure pH three times',
          handler: 'handleMeasurePH_01M'
        },
        {
          id: 7,
          title: 'Calculate Ka Values',
          shortDescription: 'Calculate three Ka values from each set of pH measurements, then average',
          handler: 'handleCalculateKa',
          infoContent: {
            title: 'Ka Calculation',
            content: `From the measured pH, calculate [H₃O⁺] = 10⁻ᵖᴴ. Then:
1. [A⁻]eq = [H₃O⁺]eq (since one mole A⁻ per mole H₃O⁺)
2. [HA]eq = [HA]initial − [H₃O⁺]eq
3. Ka = [H₃O⁺][A⁻] / [HA]

Calculate three Ka values from each solution and then an average Ka.`,
            chemicalEquation: 'CH₃COOH + H₂O ⇌ CH₃COO⁻ + H₃O⁺',
            whatHappens: 'Ka is calculated from pH measurements using Ka = [H3O+][A-]/[HA], where [H3O+] = 10^(-pH) and [A-] = [H3O+] at equilibrium for a pure weak acid solution.',
            observe: [
              'Calculate [H3O+] = 10^(-pH) for each measured pH value',
              'Calculate Ka for each concentration and average the results',
              'Compare the average Ka to the literature value of 1.76 x 10^-5',
            ],
          }
        },
        {
          id: 8,
          title: 'Part B: Acetate Ion Effect — Reference',
          shortDescription: 'In a 150 mL beaker, dilute 10 mL of 1.0 M acetic acid to 20 mL. Place 6.0 mL in clean dry beaker, measure pH.',
          handler: 'handleAcetateReference',
          infoContent: {
            title: 'Effect of Added Acetate Ion',
            content: `The dissociation of acetic acid: CH₃COOH + H₂O ⇌ CH₃COO⁻ + H₃O⁺

Predict whether adding potassium acetate to acetic acid will increase or decrease [H₃O⁺].`,
            chemicalEquation: 'CH₃COOH + H₂O ⇌ CH₃COO⁻ + H₃O⁺',
            whatHappens: 'A reference pH is measured for 0.5 M acetic acid alone, which will be compared to the same solution with added potassium acetate to observe the common ion effect on [H3O+].',
            observe: [
              'Dilute 10 mL of 1.0 M acetic acid to 20 mL, then measure pH of 6.0 mL',
              'Record this pH as the reference value for comparison with acetate-added solutions',
              'Predict whether adding acetate will increase or decrease the pH',
            ],
          }
        },
        {
          id: 9,
          title: 'Part B: Add 2.0 mL Potassium Acetate',
          shortDescription: 'In 150 mL beaker: 10 mL 1.0 M acetic acid + 2.0 mL 1.0 M potassium acetate + 8.0 mL water. Measure pH of 6.0 mL.',
          handler: 'handleAcetate2mL',
        },
        {
          id: 10,
          title: 'Part B: Add 6.0 mL Potassium Acetate',
          shortDescription: 'In 150 mL beaker: 10 mL 1.0 M acetic acid + 6.0 mL 1.0 M potassium acetate + 4.0 mL water. Measure pH.',
          handler: 'handleAcetate6mL',
        },
        {
          id: 11,
          title: 'Part C: Ka for Bisulfate Ion (HSO₄⁻)',
          shortDescription: 'Place 10 mL of 0.1 M KHSO₄ and 10 mL deionised water in 150 mL beaker. Measure pH of 6.0 mL. Calculate Ka for HSO₄⁻.',
          handler: 'handleHSO4Analysis',
          infoContent: {
            title: 'HSO₄⁻ Dissociation',
            content: `KHSO₄ is a strong electrolyte: KHSO₄ → K⁺ + HSO₄⁻. The bisulphate ion acts as an acid:

H₂O + HSO₄⁻ ⇌ H₃O⁺ + SO₄²⁻

Ka = [H₃O⁺][SO₄²⁻] / [HSO₄⁻]

Place **10 mL of 0.1 M potassium bisulphate** solution and **10 mL of deionised water** in a 150 mL beaker. Measure the pH of **6.0 mL** of this solution and calculate [H₃O⁺] and the value of Ka for HSO₄⁻.`,
            chemicalEquation: 'H₂O + HSO₄⁻ ⇌ H₃O⁺ + SO₄²⁻',
            whatHappens: 'KHSO4 fully dissociates to give HSO4-, which then partially dissociates as a weak acid; the pH measurement allows calculation of Ka for the bisulfate ion.',
            observe: [
              'Dilute 10 mL of 0.1 M KHSO4 with 10 mL deionised water',
              'Measure the pH of 6.0 mL of this solution',
              'The pH should be acidic, reflecting the partial dissociation of HSO4-',
            ],
          }
        }
      ]
    }
  ],
  quiz: {
    experimentId: 'exp10',
    title: 'Chemical Equilibrium Quiz',
    questions: [
      {
        id: 'exp10-q1',
        type: 'mcq' as const,
        question: 'According to Le Chatelier\'s Principle, what happens when Fe\u00B3⁺ is added to the FeSCN\u00B2⁺ equilibrium?',
        options: [
          { id: 'a', text: 'The solution becomes colorless' },
          { id: 'b', text: 'Equilibrium shifts right, producing more FeSCN\u00B2⁺ (darker red)' },
          { id: 'c', text: 'Equilibrium shifts left, reducing FeSCN\u00B2⁺' },
          { id: 'd', text: 'No change occurs' },
        ],
        correctOptionId: 'b',
        explanation: 'Adding Fe\u00B3⁺ increases the reactant concentration. By Le Chatelier\'s Principle, the equilibrium shifts right to consume the added Fe\u00B3⁺, producing more FeSCN\u00B2⁺ and deepening the blood-red color.',
      },
      {
        id: 'exp10-q2',
        type: 'mcq' as const,
        question: 'In the common ion effect demonstration, what is observed when concentrated HCl is added to saturated NaCl?',
        options: [
          { id: 'a', text: 'NaCl dissolves further' },
          { id: 'b', text: 'NaCl precipitates out of solution' },
          { id: 'c', text: 'The solution turns yellow' },
          { id: 'd', text: 'Gas bubbles form' },
        ],
        correctOptionId: 'b',
        explanation: 'Adding HCl introduces additional Cl⁻ ions (common ion). This shifts the NaCl dissolution equilibrium to the left, causing NaCl to precipitate.',
      },
      {
        id: 'exp10-q3',
        type: 'calculation' as const,
        question: 'If the pH of a 0.1 M acetic acid solution is measured as 2.87, calculate Ka. (Ka = [H₃O⁺]\u00B2 / (C - [H₃O⁺]), where [H₃O⁺] = 10⁻\u1D56\u1D34)',
        expectedValue: 0.0000185,
        tolerance: 10,
        unit: '',
        explanation: '[H₃O⁺] = 10⁻\u00B2·⁸⁷ = 1.35 × 10⁻\u00B3 M. Ka = (1.35×10⁻\u00B3)\u00B2 / (0.1 - 1.35×10⁻\u00B3) = 1.82×10⁻⁶ / 0.0987 = 1.85×10⁻⁵',
      },
      {
        id: 'exp10-q4',
        type: 'short_answer' as const,
        question: 'Explain why adding potassium acetate to acetic acid solution increases the pH.',
        sampleAnswer: 'Potassium acetate dissociates completely, adding acetate ions (CH₃COO⁻) to the solution. By Le Chatelier\'s Principle, the increased acetate concentration shifts the acetic acid dissociation equilibrium to the left (toward undissociated CH₃COOH), reducing [H₃O⁺] and increasing the pH. This is the common ion effect.',
      },
    ],
  },
};

export default experiment10Config;
